import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { touchpoints } from "@/lib/touchpoints";
import { Camera, Loader2, Mic, Square, X, ArrowLeft } from "lucide-react";

function VoiceRecorder({ audio, upload, onChange }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const chunks = useRef([]);
  useEffect(() => () => { if (rec) { rec.stream.getTracks().forEach((t) => t.stop()); } }, [rec]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setBusy(true);
        const file = new File(chunks.current, `voice.${mime === "audio/webm" ? "webm" : "m4a"}`, { type: mime });
        const id = await upload(file);
        if (id) onChange(id);
        setBusy(false);
      };
      mr.start();
      setRec(mr);
    } catch { toast.error("Δεν επιτράπηκε η πρόσβαση στο μικρόφωνο"); }
  };
  const stop = () => { rec?.stop(); setRec(null); };
  if (audio) {
    return (
      <div className="flex items-center gap-2" data-testid="problem-audio">
        <audio controls src={fileUrl(audio)} className="h-9 flex-1 min-w-0" />
        <button type="button" data-testid="problem-audio-remove" onClick={() => onChange("")} className="p-1 text-[#991B1B]"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <button type="button" data-testid={rec ? "problem-voice-stop" : "problem-voice-start"} onClick={rec ? stop : start} disabled={busy}
      className={`w-full rounded-xl border px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 ${rec ? "border-[#A1493A] bg-[#FBF1ED] text-[#A1493A] animate-pulse" : "border-dashed border-[#D8D0C2] text-[#6F6A60]"}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : rec ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      {busy ? "Αποθήκευση…" : rec ? "Διακοπή ηχογράφησης" : "Φωνητικό μήνυμα"}
    </button>
  );
}

function PhotoPick({ photo, upload, onChange }) {
  const [busy, setBusy] = useState(false);
  if (photo) {
    return (
      <div className="relative w-fit">
        <img src={fileUrl(photo)} alt="" className="h-24 w-24 rounded-xl object-cover border border-[#E3DED4]" />
        <button type="button" data-testid="problem-photo-remove" onClick={() => onChange("")} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-[#E3DED4] flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <label data-testid="problem-photo" className="w-full cursor-pointer rounded-xl border border-dashed border-[#D8D0C2] px-4 py-3 text-sm font-semibold text-[#6F6A60] flex items-center justify-center gap-2">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Φωτογραφία
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return; setBusy(true);
        const id = await upload(f); if (id) onChange(id);
        setBusy(false); e.target.value = "";
      }} />
    </label>
  );
}

export function ProblemDialog({ area, upload, onSave, onClose }) {
  const [item, setItem] = useState("");
  const [f, setF] = useState({ description: "", photo: "", audio: "" });
  const items = touchpoints(area || "");
  return (
    <Dialog open={!!area} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="problem-dialog">
        <DialogHeader>
          <DialogTitle>{area}{item ? ` · ${item}` : ""}</DialogTitle>
          <DialogDescription>{item ? "Περιγράψτε το πρόβλημα, βγάλτε φωτογραφία ή αφήστε φωνητικό μήνυμα." : "Επιλέξτε πού είναι το πρόβλημα."}</DialogDescription>
        </DialogHeader>
        {!item ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="problem-touchpoints">
            {items.map(([label, Icon]) => (
              <button key={label} type="button" data-testid={`touch-${label}`} onClick={() => setItem(label)}
                className="rounded-2xl border border-[#E3DED4] bg-white p-3 flex flex-col items-center gap-1.5 text-center hover:border-[#6E7A63] hover:bg-[#EEF3EE] transition-colors">
                <Icon className="h-7 w-7 text-[#6E7A63]" />
                <span className="text-xs font-medium text-[#45423C] leading-tight">{label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <button type="button" data-testid="problem-back" onClick={() => setItem("")} className="text-xs text-[#6F6A60] inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Αλλαγή σημείου</button>
            <textarea data-testid="problem-desc" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Περιγραφή προβλήματος"
              className="w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
            <PhotoPick photo={f.photo} upload={upload} onChange={(photo) => setF((p) => ({ ...p, photo }))} />
            <VoiceRecorder audio={f.audio} upload={upload} onChange={(audio) => setF((p) => ({ ...p, audio }))} />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white -mx-6 px-6 pb-1 border-t border-[#E3DED4]">
          <button type="button" data-testid="problem-cancel" onClick={onClose} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
          {item && (
            <button type="button" data-testid="problem-save" onClick={() => onSave({ area, item, ...f })}
              className="rounded-xl bg-[#6E7A63] text-white px-5 py-2.5 text-sm font-semibold">Αποθήκευση</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
